import React, { useState, useEffect, useCallback } from 'react';
import { dealsAPI, contactsAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import softphone from '../services/softphone';
import { FiPlus, FiX, FiTrash2, FiDollarSign, FiTrendingUp, FiTarget, FiPhoneCall, FiUser } from 'react-icons/fi';
import { formatDate } from '../utils/formatters';
import toast from 'react-hot-toast';

function fmtAmount(a, c = 'UZS') {
  const n = Number(a || 0);
  return `${n.toLocaleString('uz-UZ')} ${c}`;
}

export default function DealsPage() {
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const [columns, setColumns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [view, setView] = useState('open');       // 'open' (pipeline) | 'won' | 'lost'
  const [closedDeals, setClosedDeals] = useState([]);

  const allStages = columns.map(c => c.stage);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([dealsAPI.board(), dealsAPI.stats().catch(() => null)]);
      setColumns(b.data.columns || []);
      if (s) setStats(s.data.stats);
      if (view !== 'open') {
        const l = await dealsAPI.list({ status: view, limit: 200 });
        setClosedDeals(l.data.deals || []);
      }
    } catch (e) { toast.error('Bitimlarni olishda xato'); }
    finally { setLoading(false); }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  // Bitimni boshqa bosqichga ko'chirish (ham drag, ham dropdown chaqiradi)
  const changeStage = async (id, stageId) => {
    if (!id || !stageId) return;
    const cur = columns.find(c => c.deals.some(d => d.id === id));
    if (cur && cur.stage.id === Number(stageId)) return;
    const sid = Number(stageId);
    // Optimistik ko'chirish
    setColumns(prev => {
      let moved;
      const cleared = prev.map(c => ({ ...c, deals: c.deals.filter(d => (d.id === id ? (moved = d, false) : true)) }));
      return cleared.map(c => c.stage.id === sid && moved ? { ...c, deals: [{ ...moved, stage_id: sid }, ...c.deals] } : c);
    });
    try { await dealsAPI.moveStage(id, sid); load(); }
    catch { toast.error('Ko\'chirishda xato'); load(); }
  };

  const onDrop = (stageId) => {
    setOverStage(null);
    const id = dragId; setDragId(null);
    changeStage(id, stageId);
  };

  // Bitim kontaktiga qo'ng'iroq (operator softphone orqali)
  const callContact = (phone) => {
    if (!phone) return;
    softphone.triggerCall(phone);
  };

  const remove = async (id) => {
    if (!window.confirm("Bitim o'chirilsinmi?")) return;
    try { await dealsAPI.delete(id); load(); } catch { toast.error('Xato'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bitimlar</h1>
          <p className="page-subtitle">Sotuv pipeline</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}><FiPlus size={16} /> Bitim</button>
      </div>

      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <Stat icon={FiDollarSign} label="Ochiq bitimlar" value={`${stats.open_count} ta`} sub={fmtAmount(stats.open_value)} color="#3b82f6" />
          <Stat icon={FiTrendingUp} label="Bu oy yutilgan" value={`${stats.won_month_count} ta`} sub={fmtAmount(stats.won_month_value)} color="#10b981" />
          <Stat icon={FiTarget} label="Konversiya (oy)" value={`${stats.conversion_rate}%`} color="#8b5cf6" />
        </div>
      )}

      {/* Ko'rinish: Pipeline (ochiq) / Yutilgan / Yo'qotilgan */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'open', label: 'Pipeline', color: '#3b82f6' },
          { key: 'won', label: 'Yutilgan', color: '#10b981' },
          { key: 'lost', label: "Yo'qotilgan", color: '#ef4444' },
        ].map(v => (
          <button key={v.key} onClick={() => setView(v.key)} style={{
            padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            background: view === v.key ? v.color : 'var(--bg-secondary,#1e293b)',
            border: `1px solid ${view === v.key ? v.color : 'var(--border,#2d3a4f)'}`,
            color: view === v.key ? '#fff' : 'var(--text-secondary)',
          }}>{v.label}</button>
        ))}
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div> : view === 'open' ? (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, alignItems: 'flex-start' }}>
          {columns.map(col => {
            const total = col.deals.reduce((s, d) => s + Number(d.amount || 0), 0);
            return (
              <div key={col.stage.id}
                onDragOver={e => { e.preventDefault(); setOverStage(col.stage.id); }}
                onDragLeave={() => setOverStage(o => o === col.stage.id ? null : o)}
                onDrop={() => onDrop(col.stage.id)}
                style={{
                  minWidth: 250, maxWidth: 250, flex: '0 0 auto',
                  background: overStage === col.stage.id ? 'var(--accent-light, #1d2942)' : 'var(--bg-secondary, #1e293b)',
                  border: `1px solid ${overStage === col.stage.id ? col.stage.color : 'var(--border, #2d3a4f)'}`,
                  borderRadius: 12, padding: 10, transition: 'background .15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '2px 4px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: col.stage.color }} />
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{col.stage.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{col.deals.length}</span>
                </div>
                {total > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, padding: '0 4px' }}>{fmtAmount(total)}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 40 }}>
                  {col.deals.map(d => (
                    <div key={d.id} draggable
                      onDragStart={(e) => { if (e.target.closest('select, button')) { e.preventDefault(); return; } setDragId(d.id); }}
                      onDragEnd={() => setDragId(null)}
                      style={{
                        background: 'var(--bg-primary, #0f172a)', border: '1px solid var(--border, #2d3a4f)',
                        borderRadius: 10, padding: 10, cursor: 'grab', opacity: dragId === d.id ? 0.4 : 1,
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{d.title}</span>
                        <button className="btn btn-ghost btn-icon" onClick={() => remove(d.id)}
                          style={{ color: 'var(--text-muted)', padding: 2 }}><FiTrash2 size={13} /></button>
                      </div>
                      {Number(d.amount) > 0 && <div style={{ fontSize: 13, color: '#10b981', fontWeight: 700, marginTop: 4 }}>{fmtAmount(d.amount, d.currency)}</div>}
                      {(d.contact_name || d.contact_phone) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                          <FiUser size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.contact_name || d.contact_phone}
                          </span>
                          {isOperator && d.contact_phone && (
                            <button className="btn btn-secondary btn-sm" title="Qo'ng'iroq qilish"
                              onClick={() => callContact(d.contact_phone)} style={{ padding: '2px 8px', flexShrink: 0 }}>
                              <FiPhoneCall size={12} />
                            </button>
                          )}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                        <span>{d.owner_name || ''}</span>
                        {d.expected_close_date && <span>{formatDate(d.expected_close_date)}</span>}
                      </div>
                      {allStages.length > 0 && (
                        <select className="input" value={d.stage_id} onChange={(e) => changeStage(d.id, e.target.value)}
                          title="Bosqichni o'zgartirish"
                          style={{ marginTop: 8, fontSize: 12, padding: '6px 8px', height: 'auto', width: '100%' }}>
                          {allStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <ClosedDeals deals={closedDeals} stages={allStages} isOperator={isOperator}
          onCall={callContact} onMove={changeStage} onRemove={remove} status={view} />
      )}

      {showForm && <DealForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

// Yopilgan (yutilgan/yo'qotilgan) bitimlar ro'yxati — kanban'да ko'rinmaydiganlar shu yerда.
function ClosedDeals({ deals, stages, isOperator, onCall, onMove, onRemove, status }) {
  if (!deals || deals.length === 0) {
    return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
      {status === 'won' ? 'Yutilgan bitimlar yo\'q' : 'Yo\'qotilgan bitimlar yo\'q'}
    </div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {deals.map(d => (
        <div key={d.id} style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
          background: 'var(--bg-secondary, #1e293b)', border: '1px solid var(--border, #2d3a4f)', borderRadius: 10,
        }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: status === 'won' ? '#10b981' : '#ef4444', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{d.title}</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {Number(d.amount) > 0 && <span style={{ color: '#10b981', fontWeight: 600 }}>{fmtAmount(d.amount, d.currency)}</span>}
              {(d.contact_name || d.contact_phone) && <span><FiUser size={11} style={{ verticalAlign: -1, marginRight: 3 }} />{d.contact_name || d.contact_phone}</span>}
              {d.owner_name && <span>{d.owner_name}</span>}
              {d.closed_at && <span>Yopilgan: {formatDate(d.closed_at)}</span>}
              {d.status === 'lost' && d.lost_reason && <span>Sabab: {d.lost_reason}</span>}
            </div>
          </div>
          {isOperator && d.contact_phone && (
            <button className="btn btn-secondary btn-sm" title="Qo'ng'iroq qilish" onClick={() => onCall(d.contact_phone)} style={{ flexShrink: 0 }}>
              <FiPhoneCall size={13} />
            </button>
          )}
          {stages.length > 0 && (
            <select className="input" value={d.stage_id} onChange={(e) => onMove(d.id, e.target.value)} title="Bosqichni o'zgartirish (qayta ochish)"
              style={{ fontSize: 12, padding: '6px 8px', height: 'auto', maxWidth: 150, flexShrink: 0 }}>
              {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <button className="btn btn-ghost btn-icon" onClick={() => onRemove(d.id)} style={{ color: 'var(--text-muted)', flexShrink: 0 }} title="O'chirish">
            <FiTrash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-secondary, #1e293b)', border: '1px solid var(--border, #2d3a4f)', borderRadius: 12, minWidth: 190 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 800 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sub}</div>}
      </div>
    </div>
  );
}

function DealForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', amount: '', currency: 'UZS', expected_close_date: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Kontakt tanlash (qidiruvli)
  const [contact, setContact] = useState(null);
  const [cQuery, setCQuery] = useState('');
  const [cResults, setCResults] = useState([]);
  const [cSearching, setCSearching] = useState(false);

  useEffect(() => {
    if (contact) return;
    const q = cQuery.trim();
    if (!q) { setCResults([]); return; }
    setCSearching(true);
    const timer = setTimeout(async () => {
      try { const res = await contactsAPI.list({ search: q, limit: 8 }); setCResults(res.data.contacts || []); }
      catch { setCResults([]); }
      finally { setCSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [cQuery, contact]);

  const pickContact = (c) => { setContact(c); setCQuery(''); setCResults([]); };
  const clearContact = () => { setContact(null); setCQuery(''); setCResults([]); };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Sarlavha majburiy'); return; }
    setSaving(true);
    try {
      await dealsAPI.create({
        title: form.title, amount: form.amount ? Number(form.amount) : 0, currency: form.currency,
        contact_id: contact ? contact.id : null,
        expected_close_date: form.expected_close_date || null,
      });
      toast.success('Bitim yaratildi');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Xato'); }
    finally { setSaving(false); }
  };

  return (
    <div style={fStyles.overlay} onClick={onClose}>
      <div style={fStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={fStyles.header}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Yangi bitim</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input className="input" placeholder="Bitim nomi *" value={form.title} onChange={e => set('title', e.target.value)} />
          <div style={{ display: 'flex', gap: 12 }}>
            <input className="input" type="number" placeholder="Summa" value={form.amount} onChange={e => set('amount', e.target.value)} />
            <select className="input" style={{ maxWidth: 110 }} value={form.currency} onChange={e => set('currency', e.target.value)}>
              <option>UZS</option><option>USD</option><option>EUR</option>
            </select>
          </div>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: -6 }}>Kontakt (ixtiyoriy)</label>
          {contact ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-primary,#0f172a)', border: '1px solid var(--border,#2d3a4f)', borderRadius: 8 }}>
              <FiUser size={14} color="#3b82f6" />
              <span style={{ flex: 1, fontSize: 14 }}>{contact.name}{contact.phone ? ` · ${contact.phone}` : ''}</span>
              <button className="btn btn-ghost btn-icon" onClick={clearContact} title="Bekor qilish"><FiX size={15} /></button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input className="input" placeholder="Kontakt qidirish (ism yoki raqam)..." value={cQuery} onChange={e => setCQuery(e.target.value)} />
              {(cResults.length > 0 || cSearching) && (
                <div style={fStyles.dropdown}>
                  {cSearching && cResults.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Qidirilmoqda…</div>
                  ) : cResults.map(c => (
                    <button key={c.id} type="button" style={fStyles.dropdownItem} onClick={() => pickContact(c)}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</span>
                      {c.phone && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{c.phone}</span>}
                    </button>
                  ))}
                  {!cSearching && cResults.length === 0 && cQuery.trim() && (
                    <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Topilmadi</div>
                  )}
                </div>
              )}
            </div>
          )}
          <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: -6 }}>Kutilayotgan yopilish sanasi</label>
          <input className="input" type="date" value={form.expected_close_date} onChange={e => set('expected_close_date', e.target.value)} />
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 4 }}>
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' },
  modal: { background: 'var(--bg-secondary, #1e293b)', border: '1px solid var(--border, #2d3a4f)', borderRadius: 16, padding: 24, width: 420, maxWidth: '92vw' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 10,
    background: 'var(--bg-secondary,#1e293b)', border: '1px solid var(--border,#2d3a4f)',
    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 220, overflowY: 'auto',
  },
  dropdownItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%',
    padding: '9px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border,#2d3a4f)',
    color: 'var(--text-primary)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  },
};
